'use client';

import { User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { signOutAction } from '@/app/login/actions/supabase';
import type { UserInfo, UserRole } from '@/interface/user';
import { supabase } from '@/lib/supabase/client';
import { getUserClaims } from '@/lib/utils';

export interface AuthContextValue {
  userInfo?: UserInfo | null;
  user?: User | null;
  logOut: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  userInfo: null,
  logOut: () => {},
});

export const useAuth = () => useContext(AuthContext);

export interface AuthProviderProps {
  children: React.ReactNode;
  user: User | null | undefined;
  userInfo: UserInfo | null | undefined;
}

export function AuthProvider({ children, user: cachedUser, userInfo: cachedUserInfo }: AuthProviderProps) {
  const [userInfo, setUserInfo] = useState<UserInfo | null | undefined>(cachedUserInfo);
  const [user, setUser] = useState<User | null | undefined>(cachedUser);

  const queryClient = useQueryClient();

  const logOut = useCallback(async () => {
    setUserInfo(null);
    setUser(null);
    queryClient.resetQueries();
    queryClient.removeQueries();
    queryClient.clear();
    sessionStorage.removeItem('printer_update_banner_dismissed');
    await signOutAction();
  }, [queryClient]);

  useEffect(() => {
    const subscription = supabase.auth.onAuthStateChange(async (event, session) => {
      switch (event) {
        case 'INITIAL_SESSION':
        case 'USER_UPDATED':
        case 'TOKEN_REFRESHED':
        case 'SIGNED_IN':
          if (!session) return;

          const claims = getUserClaims(session.access_token);
          const userInfo: UserInfo = {
            name: claims.user_name,
            role: claims.user_role.toUpperCase() as keyof typeof UserRole,
          };

          setUserInfo(userInfo);
          setUser(session.user);
          break;
        case 'SIGNED_OUT':
          setUserInfo(null);
          setUser(null);
          break;
      }
    });

    return () => subscription.data.subscription.unsubscribe();
  }, []);

  // useEffect(() => {
  //   const fetchSession = async () => {
  //     if (hashParams.access_token) {
  //       const { data } = await supabase.auth.setSession({
  //         access_token: hashParams.access_token,
  //         refresh_token: hashParams.refresh_token,
  //       });

  //       setSession(data.session);
  //     } else {
  //       const { data } = await supabase.auth.getSession();

  //       setSession(data.session);
  //     }
  //   };

  //   fetchSession();
  // }, [hashParams.access_token, hashParams.refresh_token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userInfo,
        logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
